import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CreditCard, Plus, Trash2, Star, StarOff, Edit2, Check, X } from 'lucide-react';
import AppLayout from '@/components/layout/AppLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useToast } from '@/contexts/ToastContext';
import { useAuth } from '@/contexts/AuthContext';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';

// ============================================================================
// TYPES
// ============================================================================

interface PaymentMethod {
  id: string;
  userId: string;
  provider: string;
  stripePaymentMethodId: string;
  stripeCustomerId: string;
  paymentType: string;
  cardBrand?: string;
  cardLastFour?: string;
  cardExpMonth?: number;
  cardExpYear?: number;
  nickname?: string;
  isDefault: boolean;
  isVerified: boolean;
  status: string;
  lastUsedAt?: string;
  createdAt: string;
  updatedAt: string;
  deletedAt?: string;
}

// Initialize Stripe
const stripePublishableKey = import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY || '';
const stripePromise = loadStripe(stripePublishableKey);

// ============================================================================
// ADD PAYMENT METHOD FORM COMPONENT
// ============================================================================

interface AddPaymentMethodFormProps {
  onSuccess: () => void;
  onCancel: () => void;
}

function AddPaymentMethodForm({ onSuccess, onCancel }: AddPaymentMethodFormProps) {
  const stripe = useStripe();
  const elements = useElements();
  const { showError, showSuccess } = useToast();
  const [isProcessing, setIsProcessing] = useState(false);
  const [setAsDefault, setSetAsDefault] = useState(false);

  // Billing information state
  const [cardholderName, setCardholderName] = useState('');
  const [billingAddress, setBillingAddress] = useState({
    line1: '',
    line2: '',
    city: '',
    state: '',
    postal_code: '',
    country: 'US',
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!stripe || !elements) {
      showError('Stripe Error', 'Stripe has not loaded yet. Please try again.');
      return;
    }

    // Validate billing information
    if (!cardholderName.trim()) {
      showError('Missing Information', 'Please enter the cardholder name');
      return;
    }

    if (!billingAddress.line1.trim() || !billingAddress.city.trim() ||
        !billingAddress.state.trim() || !billingAddress.postal_code.trim()) {
      showError('Missing Information', 'Please complete all billing address fields');
      return;
    }

    try {
      setIsProcessing(true);

      // Step 1: Create Setup Intent
      const setupIntentResponse = await fetch(`${import.meta.env.VITE_API_URL || "http://localhost:8080"}/api/workflows/payment-methods/setup-intent`, {
        method: 'POST',
        credentials: 'include',
      });

      if (!setupIntentResponse.ok) {
        throw new Error('Failed to create Setup Intent');
      }

      const { data } = await setupIntentResponse.json();
      const { clientSecret } = data;

      // Step 2: Confirm Setup Intent with card details and billing information
      const cardElement = elements.getElement(CardElement);
      if (!cardElement) {
        throw new Error('Card element not found');
      }

      const { setupIntent, error } = await stripe.confirmCardSetup(clientSecret, {
        payment_method: {
          card: cardElement,
          billing_details: {
            name: cardholderName,
            address: billingAddress,
          },
        },
      });

      if (error) {
        throw new Error(error.message);
      }

      if (!setupIntent?.payment_method) {
        throw new Error('No payment method returned from Stripe');
      }

      // Step 3: Attach payment method to user
      const attachResponse = await fetch(`${import.meta.env.VITE_API_URL || "http://localhost:8080"}/api/workflows/payment-methods`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          paymentMethodId: setupIntent.payment_method,
          setAsDefault,
        }),
      });

      if (!attachResponse.ok) {
        throw new Error('Failed to attach payment method');
      }

      showSuccess('Payment Method Added', 'Your payment method has been added successfully');
      onSuccess();
    } catch (error: any) {
      console.error('Error adding payment method:', error);
      showError('Failed to Add Payment Method', error.message || 'An error occurred');
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <Card className="p-6">
      <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
        <CreditCard className="h-5 w-5" />
        Add Payment Method
      </h3>

      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Cardholder Name */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Name on Card *
          </label>
          <input
            type="text"
            value={cardholderName}
            onChange={(e) => setCardholderName(e.target.value)}
            placeholder="John Doe"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            required
          />
        </div>

        {/* Card Element */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Card Information *
          </label>
          <div className="p-4 border border-gray-300 rounded-lg">
            <CardElement
              options={{
                style: {
                  base: {
                    fontSize: '16px',
                    color: '#424770',
                    '::placeholder': {
                      color: '#aab7c4',
                    },
                  },
                  invalid: {
                    color: '#9e2146',
                  },
                },
              }}
            />
          </div>
        </div>

        {/* Billing Address */}
        <div className="space-y-3">
          <h4 className="text-sm font-medium text-gray-700">Billing Address</h4>

          {/* Address Line 1 */}
          <div>
            <label className="block text-xs text-gray-600 mb-1">Street Address *</label>
            <input
              type="text"
              value={billingAddress.line1}
              onChange={(e) => setBillingAddress({ ...billingAddress, line1: e.target.value })}
              placeholder="123 Main Street"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
            />
          </div>

          {/* Address Line 2 */}
          <div>
            <label className="block text-xs text-gray-600 mb-1">Apartment, Suite, etc. (Optional)</label>
            <input
              type="text"
              value={billingAddress.line2}
              onChange={(e) => setBillingAddress({ ...billingAddress, line2: e.target.value })}
              placeholder="Apt 4B"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {/* City, State, Zip */}
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2 sm:col-span-1">
              <label className="block text-xs text-gray-600 mb-1">City *</label>
              <input
                type="text"
                value={billingAddress.city}
                onChange={(e) => setBillingAddress({ ...billingAddress, city: e.target.value })}
                placeholder="New York"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>

            <div className="col-span-2 sm:col-span-1">
              <label className="block text-xs text-gray-600 mb-1">State *</label>
              <input
                type="text"
                value={billingAddress.state}
                onChange={(e) => setBillingAddress({ ...billingAddress, state: e.target.value })}
                placeholder="NY"
                maxLength={2}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs text-gray-600 mb-1">ZIP Code *</label>
              <input
                type="text"
                value={billingAddress.postal_code}
                onChange={(e) => setBillingAddress({ ...billingAddress, postal_code: e.target.value })}
                placeholder="10001"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>

            <div>
              <label className="block text-xs text-gray-600 mb-1">Country</label>
              <select
                value={billingAddress.country}
                onChange={(e) => setBillingAddress({ ...billingAddress, country: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="US">United States</option>
                <option value="CA">Canada</option>
              </select>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-2 pt-2">
          <input
            type="checkbox"
            id="setAsDefault"
            checked={setAsDefault}
            onChange={(e) => setSetAsDefault(e.target.checked)}
            className="rounded"
          />
          <label htmlFor="setAsDefault" className="text-sm text-gray-700">
            Set as default payment method
          </label>
        </div>

        <div className="flex gap-2">
          <Button
            type="submit"
            disabled={!stripe || isProcessing}
            className="flex-1"
          >
            {isProcessing ? 'Processing...' : 'Add Payment Method'}
          </Button>
          <Button
            type="button"
            variant="outline"
            onClick={onCancel}
            disabled={isProcessing}
          >
            Cancel
          </Button>
        </div>
      </form>
    </Card>
  );
}

// ============================================================================
// PAYMENT METHOD CARD COMPONENT
// ============================================================================

interface PaymentMethodCardProps {
  paymentMethod: PaymentMethod;
  onSetDefault: (id: string) => void;
  onDelete: (id: string) => void;
  onUpdateNickname: (id: string, nickname: string) => void;
  isProcessing: boolean;
}

function PaymentMethodCard({
  paymentMethod,
  onSetDefault,
  onDelete,
  onUpdateNickname,
  isProcessing,
}: PaymentMethodCardProps) {
  const [isEditingNickname, setIsEditingNickname] = useState(false);
  const [nickname, setNickname] = useState(paymentMethod.nickname || '');

  const handleSaveNickname = () => {
    if (nickname.trim() !== (paymentMethod.nickname || '')) {
      onUpdateNickname(paymentMethod.id, nickname.trim());
    }
    setIsEditingNickname(false);
  };

  const handleCancelEdit = () => {
    setNickname(paymentMethod.nickname || '');
    setIsEditingNickname(false);
  };

  const getCardBrandIcon = (brand?: string) => {
    // You can add card brand icons here
    return brand?.toUpperCase() || 'CARD';
  };

  return (
    <Card className="p-4">
      <div className="flex items-start justify-between">
        <div className="flex items-start gap-3 flex-1">
          <div className="p-2 bg-blue-100 rounded-lg">
            <CreditCard className="h-6 w-6 text-blue-600" />
          </div>

          <div className="flex-1">
            <div className="flex items-center gap-2 mb-1">
              {isEditingNickname ? (
                <div className="flex items-center gap-2">
                  <input
                    type="text"
                    value={nickname}
                    onChange={(e) => setNickname(e.target.value)}
                    className="px-2 py-1 border border-gray-300 rounded text-sm"
                    placeholder="Card nickname"
                    autoFocus
                  />
                  <button
                    onClick={handleSaveNickname}
                    className="p-1 text-green-600 hover:bg-green-50 rounded"
                    disabled={isProcessing}
                  >
                    <Check className="h-4 w-4" />
                  </button>
                  <button
                    onClick={handleCancelEdit}
                    className="p-1 text-gray-600 hover:bg-gray-50 rounded"
                    disabled={isProcessing}
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              ) : (
                <>
                  <h4 className="font-semibold text-gray-900">
                    {paymentMethod.nickname || `${getCardBrandIcon(paymentMethod.cardBrand)} •••• ${paymentMethod.cardLastFour}`}
                  </h4>
                  <button
                    onClick={() => setIsEditingNickname(true)}
                    className="p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-50 rounded"
                    disabled={isProcessing}
                  >
                    <Edit2 className="h-3 w-3" />
                  </button>
                </>
              )}

              {paymentMethod.isDefault && (
                <span className="px-2 py-0.5 bg-yellow-100 text-yellow-800 text-xs font-medium rounded-full flex items-center gap-1">
                  <Star className="h-3 w-3" />
                  Default
                </span>
              )}
            </div>

            <div className="text-sm text-gray-600 space-y-1">
              <p>
                {paymentMethod.cardBrand?.toUpperCase()} ending in {paymentMethod.cardLastFour}
              </p>
              <p>
                Expires {paymentMethod.cardExpMonth}/{paymentMethod.cardExpYear}
              </p>
              {paymentMethod.lastUsedAt && (
                <p className="text-xs text-gray-500">
                  Last used: {new Date(paymentMethod.lastUsedAt).toLocaleDateString()}
                </p>
              )}
            </div>
          </div>
        </div>

        <div className="flex gap-2">
          {!paymentMethod.isDefault && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => onSetDefault(paymentMethod.id)}
              disabled={isProcessing}
              className="flex items-center gap-1"
            >
              <StarOff className="h-4 w-4" />
              Set Default
            </Button>
          )}

          <Button
            variant="outline"
            size="sm"
            onClick={() => onDelete(paymentMethod.id)}
            disabled={isProcessing}
            className="flex items-center gap-1 text-red-600 hover:text-red-700 hover:bg-red-50"
          >
            <Trash2 className="h-4 w-4" />
            Delete
          </Button>
        </div>
      </div>
    </Card>
  );
}

// ============================================================================
// MAIN PAYMENT METHODS PAGE COMPONENT
// ============================================================================

export function PaymentMethodsPage() {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const { showError, showSuccess, showInfo } = useToast();

  const [paymentMethods, setPaymentMethods] = useState<PaymentMethod[]>([]);
  const [isLoadingData, setIsLoadingData] = useState(true);
  const [isProcessing, setIsProcessing] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);

  // Fetch payment methods
  useEffect(() => {
    const fetchPaymentMethods = async () => {
      if (!isAuthenticated) {
        navigate('/login');
        return;
      }

      try {
        setIsLoadingData(true);

        const response = await fetch(`${import.meta.env.VITE_API_URL || "http://localhost:8080"}/api/workflows/payment-methods`, {
          credentials: 'include',
        });

        if (response.ok) {
          const result = await response.json();
          setPaymentMethods(result.data?.paymentMethods || []);
        } else {
          throw new Error('Failed to fetch payment methods');
        }
      } catch (error: any) {
        console.error('Failed to fetch payment methods:', error);
        showError('Failed to Load Payment Methods', error.message || 'An error occurred');
      } finally {
        setIsLoadingData(false);
      }
    };

    fetchPaymentMethods();
  }, [isAuthenticated, navigate, showError]);

  // Handle setting default payment method
  const handleSetDefault = async (paymentMethodId: string) => {
    try {
      setIsProcessing(true);
      showInfo('Setting default...', 'Please wait');

      const response = await fetch(
        `${import.meta.env.VITE_API_URL || "http://localhost:8080"}/api/workflows/payment-methods/${paymentMethodId}/default`,
        {
          method: 'PUT',
          credentials: 'include',
        }
      );

      if (!response.ok) {
        throw new Error('Failed to set default payment method');
      }

      showSuccess('Default Updated', 'Default payment method has been updated');

      // Refresh payment methods
      const refreshResponse = await fetch(`${import.meta.env.VITE_API_URL || "http://localhost:8080"}/api/workflows/payment-methods`, {
        credentials: 'include',
      });

      if (refreshResponse.ok) {
        const result = await refreshResponse.json();
        setPaymentMethods(result.data?.paymentMethods || []);
      }
    } catch (error: any) {
      console.error('Error setting default:', error);
      showError('Failed to Set Default', error.message || 'An error occurred');
    } finally {
      setIsProcessing(false);
    }
  };

  // Handle deleting payment method
  const handleDelete = async (paymentMethodId: string) => {
    const confirmDelete = window.confirm(
      'Are you sure you want to delete this payment method?'
    );

    if (!confirmDelete) return;

    try {
      setIsProcessing(true);
      showInfo('Deleting...', 'Please wait');

      const response = await fetch(
        `${import.meta.env.VITE_API_URL || "http://localhost:8080"}/api/workflows/payment-methods/${paymentMethodId}`,
        {
          method: 'DELETE',
          credentials: 'include',
        }
      );

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Failed to delete payment method');
      }

      showSuccess('Payment Method Deleted', 'Your payment method has been removed');

      // Refresh payment methods
      const refreshResponse = await fetch(`${import.meta.env.VITE_API_URL || "http://localhost:8080"}/api/workflows/payment-methods`, {
        credentials: 'include',
      });

      if (refreshResponse.ok) {
        const result = await refreshResponse.json();
        setPaymentMethods(result.data?.paymentMethods || []);
      }
    } catch (error: any) {
      console.error('Error deleting payment method:', error);
      showError('Failed to Delete', error.message || 'An error occurred');
    } finally {
      setIsProcessing(false);
    }
  };

  // Handle updating nickname
  const handleUpdateNickname = async (paymentMethodId: string, nickname: string) => {
    try {
      setIsProcessing(true);

      const response = await fetch(
        `${import.meta.env.VITE_API_URL || "http://localhost:8080"}/api/workflows/payment-methods/${paymentMethodId}/nickname`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
          },
          credentials: 'include',
          body: JSON.stringify({ nickname }),
        }
      );

      if (!response.ok) {
        throw new Error('Failed to update nickname');
      }

      showSuccess('Nickname Updated', 'Payment method nickname has been updated');

      // Refresh payment methods
      const refreshResponse = await fetch(`${import.meta.env.VITE_API_URL || "http://localhost:8080"}/api/workflows/payment-methods`, {
        credentials: 'include',
      });

      if (refreshResponse.ok) {
        const result = await refreshResponse.json();
        setPaymentMethods(result.data?.paymentMethods || []);
      }
    } catch (error: any) {
      console.error('Error updating nickname:', error);
      showError('Failed to Update Nickname', error.message || 'An error occurred');
    } finally {
      setIsProcessing(false);
    }
  };

  // Handle successful payment method addition
  const handleAddSuccess = async () => {
    setShowAddForm(false);

    // Refresh payment methods
    try {
      const response = await fetch(`${import.meta.env.VITE_API_URL || "http://localhost:8080"}/api/workflows/payment-methods`, {
        credentials: 'include',
      });

      if (response.ok) {
        const result = await response.json();
        setPaymentMethods(result.data?.paymentMethods || []);
      }
    } catch (error) {
      console.error('Error refreshing payment methods:', error);
    }
  };

  if (isLoadingData) {
    return (
      <AppLayout title="Payment Methods" backLink="/account">
        <div className="max-w-4xl mx-auto p-6">
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Loading payment methods...</p>
          </div>
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout title="Payment Methods" backLink="/account">
      <div className="max-w-4xl mx-auto p-6">
        {/* Subtitle */}
        <div className="mb-8">
          <p className="text-gray-600">
            Manage your payment methods for subscriptions and purchases
          </p>
        </div>

        {/* Add Payment Method Section */}
        {showAddForm ? (
          <div className="mb-6">
            <Elements stripe={stripePromise}>
              <AddPaymentMethodForm
                onSuccess={handleAddSuccess}
                onCancel={() => setShowAddForm(false)}
              />
            </Elements>
          </div>
        ) : (
          <div className="mb-6">
            <Button
              onClick={() => setShowAddForm(true)}
              className="flex items-center gap-2"
              disabled={isProcessing}
            >
              <Plus className="h-5 w-5" />
              Add Payment Method
            </Button>
          </div>
        )}

        {/* Payment Methods List */}
        <div className="space-y-4">
          {paymentMethods.length === 0 ? (
            <Card className="p-12 text-center">
              <CreditCard className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                No Payment Methods
              </h3>
              <p className="text-gray-600 mb-4">
                Add a payment method to subscribe to plans and make purchases
              </p>
              {!showAddForm && (
                <Button
                  onClick={() => setShowAddForm(true)}
                  className="flex items-center gap-2 mx-auto"
                >
                  <Plus className="h-5 w-5" />
                  Add Your First Payment Method
                </Button>
              )}
            </Card>
          ) : (
            paymentMethods.map((paymentMethod) => (
              <PaymentMethodCard
                key={paymentMethod.id}
                paymentMethod={paymentMethod}
                onSetDefault={handleSetDefault}
                onDelete={handleDelete}
                onUpdateNickname={handleUpdateNickname}
                isProcessing={isProcessing}
              />
            ))
          )}
        </div>

        {/* Info Section */}
        {paymentMethods.length > 0 && (
          <Card className="mt-8 p-6 bg-blue-50 border-blue-200">
            <h3 className="font-semibold text-blue-900 mb-2">Payment Method Information</h3>
            <ul className="text-sm text-blue-800 space-y-1">
              <li>• Your default payment method will be used for automatic subscription renewals</li>
              <li>• All payment information is securely stored and encrypted</li>
              <li>• You can update or remove payment methods at any time</li>
              <li>• Card details are processed securely through Stripe</li>
            </ul>
          </Card>
        )}
      </div>
    </AppLayout>
  );
}

export default PaymentMethodsPage;
